import { ReactNode, useState } from 'react';
import {
  Catalog,
  CatalogCourt,
  findCourt,
  grauLabel,
  groupBySystem,
  listTribunals,
  suggestCourts,
  tribunalNeedsGrau,
} from '../../utils/catalog';

interface CourtPickerProps {
  catalog: Catalog | null;
  catalogError: boolean;
  /** catalog_key atual da credencial (null = "Outro site" ou escolha incompleta). */
  value: string | null;
  /** URLs digitadas/salvas, usadas só para sugerir um tribunal (nunca vincula sozinho). */
  urlsForSuggestion: string[];
  /** Devolve o tribunal completo, ou null em "Outro site" / escolha incompleta. */
  onChange: (court: CatalogCourt | null) => void;
  /** Campos de URL: só aparecem em "Outro site". */
  children?: ReactNode;
  /** D23: campo mostrado logo depois do sistema/tribunal/grau, antes das URLs (ex.: o "Titular"). */
  depoisDoTribunal?: ReactNode;
}

interface Rascunho {
  system: string;
  tribunal: string;
  grau: string;
}

const estiloCampo = {
  background: 'var(--color-bg-root)',
  border: '1px solid var(--color-border-default)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-primary)',
  width: '100%',
  boxSizing: 'border-box',
} as const;

const estiloRotulo = { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' } as const;
const estiloNota = { fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: 1.4 } as const;

export function CourtPicker({ catalog, catalogError, value, urlsForSuggestion, onChange, children, depoisDoTribunal }: CourtPickerProps) {
  const [rascunho, setRascunho] = useState<Rascunho>({ system: '', tribunal: '', grau: '' });

  const courtAtual = findCourt(catalog, value);
  // Com vínculo resolvido, o catálogo manda; senão vale o que a pessoa já escolheu na tela.
  const escolha: Rascunho = courtAtual
    ? { system: courtAtual.system, tribunal: courtAtual.tribunal, grau: courtAtual.grau ?? '' }
    : rascunho;

  const sistemas = catalog ? groupBySystem(catalog) : [];
  const tribunais = catalog && escolha.system ? listTribunals(catalog, escolha.system) : [];
  const tribunalEscolhido = tribunais.find((t) => t.id === escolha.tribunal) ?? null;
  const pedeGrau = !!tribunalEscolhido && tribunalNeedsGrau(tribunalEscolhido);

  const escolher = (system: string, tribunal: string, grau: string) => {
    setRascunho({ system, tribunal, grau });
    if (!catalog || !system || !tribunal) return onChange(null);
    const opcao = listTribunals(catalog, system).find((t) => t.id === tribunal);
    if (!opcao) return onChange(null);
    onChange(tribunalNeedsGrau(opcao) ? opcao.courts.find((c) => c.grau === grau) ?? null : opcao.courts[0]);
  };

  const modoOutroSite = !value && escolha.system === '';
  const sugestoes = modoOutroSite && catalog ? suggestCourts(catalog, urlsForSuggestion).slice(0, 3) : [];
  const aguardandoCatalogo = !catalog && !catalogError;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <label style={estiloRotulo}>Sistema</label>
        <select
          value={escolha.system}
          disabled={aguardandoCatalogo || (!catalog && !modoOutroSite)}
          onChange={(e) => escolher(e.target.value, '', '')}
          style={estiloCampo}
        >
          <option value="">{aguardandoCatalogo ? 'Carregando catálogo...' : 'Outro site (informar URL)'}</option>
          {sistemas.map((s) => (
            <option key={s.system} value={s.system}>{s.label}</option>
          ))}
        </select>

        {catalogError && !catalog && (
          <span style={estiloNota}>Não foi possível carregar a lista de tribunais. Use "Outro site" e informe a URL.</span>
        )}
        {value && !courtAtual && catalog && (
          <span style={{ ...estiloNota, color: 'var(--color-error)' }}>
            Este vínculo ({value}) não existe mais no catálogo. Escolha outro sistema abaixo ou mantenha como está.
          </span>
        )}

        {escolha.system !== '' && catalog && (
          <>
            <label style={estiloRotulo}>Tribunal</label>
            <select
              required
              value={escolha.tribunal}
              onChange={(e) => escolher(escolha.system, e.target.value, '')}
              style={estiloCampo}
            >
              <option value="">Selecione o tribunal</option>
              {tribunais.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </>
        )}

        {pedeGrau && tribunalEscolhido && (
          <>
            <label style={estiloRotulo}>Grau</label>
            <select
              required
              value={escolha.grau}
              onChange={(e) => escolher(escolha.system, escolha.tribunal, e.target.value)}
              style={estiloCampo}
            >
              <option value="">Selecione o grau</option>
              {tribunalEscolhido.courts.map((c) => (
                <option key={c.key} value={c.grau ?? ''}>{grauLabel(c.grau)}</option>
              ))}
            </select>
          </>
        )}

        {courtAtual && <span style={estiloNota}>Selecionado: {courtAtual.label}</span>}

        {sugestoes.length > 0 && (
          <div style={{ ...estiloNota, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span>Parece ser:</span>
            {sugestoes.map((court) => (
              <button
                key={court.key}
                type="button"
                onClick={() => escolher(court.system, court.tribunal, court.grau ?? '')}
                style={{ background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-primary-400)', padding: '4px 8px', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: '12px' }}
              >
                Vincular a {court.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {depoisDoTribunal}
      {modoOutroSite && children}
    </>
  );
}
